import React, { useState } from 'react';
import {
  Input,
  Button,
  Card,
  Typography,
  Alert,
  Flex,
  Space,
  Radio,
} from 'antd';
import {
  MailOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import logo from '../../assets/logo.png';
import { useTranslation } from '../../i18n/TranslationContext';

const { Text } = Typography;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [useEmail, setUseEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = useEmail
        ? { email: email.trim() }
        : { user_name: username.trim() };

      if (useEmail && !email.trim()) {
        setError(t('auth.enterEmailAddress'));
        setLoading(false);
        return;
      }
      if (!useEmail && !username.trim()) {
        setError(t('auth.enterUsernameRequired'));
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_CONFIG.FULL_API_URL}/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-application-type': 'bidmanager-admin-panel',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || t('auth.provideEmailOrUsername'));
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError(t('auth.provideEmailOrUsername'));
      } else {
        setError(t('auth.requestFailedLater'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Flex
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorations */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(100px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-15%',
          left: '-10%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          filter: 'blur(100px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '30%',
          height: '30%',
          borderRadius: '50%',
          background: 'rgba(30, 58, 95, 0.3)',
          filter: 'blur(120px)',
        }}
      />

      <Flex
        style={{ position: 'relative', zIndex: 1, width: '100%', padding: 24 }}
        justify="center"
        align="center"
      >
        <Card
          style={{
            maxWidth: 480,
            width: '100%',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            borderRadius: 12,
          }}
          styles={{ body: { padding: 40 } }}
        >
          {success ? (
            <>
              <Flex vertical align="center" gap={24} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    padding: 24,
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(30, 58, 95, 0.3)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <img src={logo} alt="Bidmanager" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                </div>
              </Flex>

              <Flex vertical align="center" gap={16} style={{ marginBottom: 24 }}>
                <CheckCircleOutlined style={{ fontSize: 64, color: '#22c55e' }} />
                <Text strong style={{ fontSize: 20, color: '#333' }}>
                  {t('auth.checkEmailTitle')}
                </Text>
                <Text type="secondary" style={{ textAlign: 'center', lineHeight: 1.6 }}>
                  {t('auth.checkEmailBody')}
                </Text>
              </Flex>

              <Button
                type="primary"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToLogin}
                block
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                }}
              >
                {t('auth.backToLogin')}
              </Button>
            </>
          ) : (
            <>
              <Flex vertical align="center" gap={16} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    padding: 24,
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(30, 58, 95, 0.3)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <img src={logo} alt="Bidmanager" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                </div>
                <Text
                  strong
                  style={{
                    fontSize: 16,
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('auth.forgotPasswordTitle')}
                </Text>
                <Text type="secondary">
                  {t('auth.forgotPasswordHint')}
                </Text>
              </Flex>

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <form onSubmit={handleSubmit}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Radio.Group
                    value={useEmail ? 'email' : 'username'}
                    onChange={(e) => {
                      setUseEmail(e.target.value === 'email');
                      setError('');
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <Radio.Button value="email">{t('auth.resetByEmail')}</Radio.Button>
                    <Radio.Button value="username">{t('auth.resetByUsername')}</Radio.Button>
                  </Radio.Group>

                  {useEmail ? (
                    <Input
                      size="large"
                      placeholder={t('auth.emailPlaceholder')}
                      prefix={<MailOutlined />}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      required
                      disabled={loading}
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  ) : (
                    <Input
                      size="large"
                      placeholder={t('auth.forgotUsernamePlaceholder')}
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        setError('');
                      }}
                      required
                      disabled={loading}
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  )}

                  <Space style={{ width: '100%', marginTop: 16 }} size="middle">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<MailOutlined />}
                      loading={loading}
                      disabled={useEmail ? !email.trim() : !username.trim()}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                      }}
                    >
                      {loading ? t('auth.sending') : t('auth.sendResetEmail')}
                    </Button>
                    <Button
                      onClick={handleBackToLogin}
                      disabled={loading}
                      style={{ flex: 1, borderColor: '#1e3a5f', color: '#1e3a5f' }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Space>
                </Space>
              </form>
            </>
          )}
        </Card>
      </Flex>
    </Flex>
  );
};

export default ForgotPassword;
